import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { hitArea } from "./hit";
import { KICKER_STYLE } from "./Kicker";
import { BUTTON_RESET, PRESS_CLASS } from "./press";

export interface BackProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  /** The control's copy — always `t('…')`, never a literal. */
  label: string;
  onClick: () => void;
  /** Optional far-end slot; in the handoff this is always a `ShareBtn`. */
  right?: ReactNode;
}

/* The bar: padding 24 24 0, space-between, and above the stage canvases
 * (which sit at z-index 1–2 inside the scroller). */
const BAR: CSSProperties = {
  padding: "var(--space-6) var(--space-6) 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  zIndex: 2,
};

/* The control: the kicker type one step brighter than a kicker. */
const CONTROL: CSSProperties = {
  ...BUTTON_RESET,
  ...KICKER_STYLE,
  color: "var(--d-fg-2)",
  display: "flex",
  gap: "var(--space-2)",
  alignItems: "center",
  ...hitArea(16), // 44pt tap area, DUE-419
};

const ARROW: CSSProperties = { font: "400 13px var(--font-body)" };

/**
 * The back bar at the top of every screen that is not a tab root.
 *
 * Ported from `Back` in
 * `docs/design/handoff/design/templates/friends/friends-screen.jsx` (identical
 * in `you-screen.jsx`). One deliberate difference: the prototype's `<div
 * onClick>` is a real `<button>` here, so the control is focusable and named.
 */
export function Back({
  label,
  onClick,
  right,
  className,
  style,
  ...rest
}: BackProps): JSX.Element {
  return (
    <div className={className} style={{ ...BAR, ...style }} {...rest}>
      <button
        type="button"
        className={PRESS_CLASS}
        onClick={onClick}
        style={CONTROL}
      >
        <span data-back-arrow aria-hidden="true" style={ARROW}>
          ←
        </span>
        {label}
      </button>
      {right ? <span>{right}</span> : null}
    </div>
  );
}

export default Back;
